import { useState, useEffect, useRef, useCallback, type ReactNode } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { usePermission } from "@/hooks/usePermission";
import {
    ArrowLeft,
    User,
    Plus,
    Calendar,
    AlertTriangle,
    Clock,
    NotebookPen,
    IndianRupee,
    ChevronRight,
    Bed as BedIcon,
    Paperclip,
} from "lucide-react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";

import { usePatientData } from "@/hooks/usePatientData";
import { usePrescriptionActions } from "@/hooks/usePrescriptionActions";

import PrescriptionForm from "@/components/Prescription/PrescriptionForm";
import ViewPrescriptionModal from "@/components/Prescription/PrintPrescriptionPreviewModal";
import VisitTimeline from "@/components/Patient/VisitTimeline";
import AppointmentsTab from "@/components/Appointment/AppointmentsTab";
import BillingTab from "@/components/Patient/BillingTab";
import PatientFormModal from "@/components/Patient/PatientFormModal";
import PatientIPDTab from "@/components/Patient/PatientIPDTab";
import PatientProfileTab from "@/components/Patient/PatientProfileTab";
import PatientDocuments from "@/components/PatientDocuments/PatientDocuments";
import FormActionBar from "@/components/PrescriptionForm/StickyBar";

import { getSettings } from "@/lib/api";
import { displayAge } from "@/lib/patientAge";
import { Doctor } from "@/types/doctor";

// ─── Types ────────────────────────────────────────────────────────────────────

interface PatientDetailsPageProps {
    onNavigateBack?: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function PatientDetailsPage({ onNavigateBack }: PatientDetailsPageProps) {
    const { currentUser } = useAuth();
    const { can, hasFeature } = usePermission();
    const navigate = useNavigate();
    const { id: patientId } = useParams<{ id: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState("prescriptions");
    const [showEditModal, setShowEditModal] = useState(false);

    // Doctor profile — fetched once, passed into PrescriptionForm
    const [doctor, setDoctor] = useState<Doctor | null>(null);

    // Use safe ID so hooks are always called unconditionally
    const safePatientId = patientId || "";

    const {
        patient, visits, appointments, loading,
        medicalAlerts, fetchPatientData,
    } = usePatientData(safePatientId);

    const prescriptionActions = usePrescriptionActions(safePatientId, fetchPatientData);

    const patientHeaderRef = useRef<HTMLDivElement>(null);

    // Lifted form state — drives the FormActionBar rendered at page level
    const rxSubmitRef = useRef<(() => void) | null>(null);
    const [rxFormState, setRxFormState] = useState<{
        hasChanges: boolean; isSubmitting: boolean; hasValidChanges: boolean; attachmentsSlot: ReactNode; pendingAttachmentsCount: number;
    }>({ hasChanges: false, isSubmitting: false, hasValidChanges: false, attachmentsSlot: null, pendingAttachmentsCount: 0 });

    const handleFormReady = useCallback(
        (submitFn: () => void, hasChanges: boolean, isSubmitting: boolean, hasValidChanges: boolean, attachmentsSlot: ReactNode, pendingAttachmentsCount: number) => {
            rxSubmitRef.current = submitFn;
            setRxFormState({ hasChanges, isSubmitting, hasValidChanges, attachmentsSlot, pendingAttachmentsCount });
        },
        []
    );

    // Create-flow Cancel bypasses usePrescriptionForm's own confirmation
    // entirely (it's wired straight to prescriptionActions.handlePrescriptionCancel),
    // so a staged-but-unsaved attachment would otherwise be discarded silently.
    const handleNewPrescriptionCancel = useCallback(() => {
        if (rxFormState.pendingAttachmentsCount > 0) {
            const confirmed = window.confirm(
                `You have ${rxFormState.pendingAttachmentsCount} unsaved attachment${rxFormState.pendingAttachmentsCount > 1 ? "s" : ""} that will not be saved. Are you sure you want to cancel?`
            );
            if (!confirmed) return;
        }
        prescriptionActions.handlePrescriptionCancel();
    }, [rxFormState.pendingAttachmentsCount, prescriptionActions.handlePrescriptionCancel]);

    // ── Effects ─────────────────────────────────────────────────────────────────

    useEffect(() => {
        if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
        getSettings(currentUser.doctor_id, currentUser.clinic_id)
            .then((s) => {
                if (!s) return;
                setDoctor({
                    id:            currentUser.doctor_id  ?? "",
                    name:          s.profile?.name        ?? "",
                    specialty:     s.profile?.specialty   ?? "General Medicine",
                    phone:         s.profile?.phone       ?? "",
                    email:         s.profile?.email       ?? "",
                    clinic_id:     currentUser.clinic_id  ?? "",
                    last_modified: "",
                });
            })
            .catch(() => { /* PrescriptionForm falls back to General Medicine */ });
    }, [currentUser?.doctor_id, currentUser?.clinic_id]);

    // Auto-open prescription form when URL has ?openPrescription=true
    const { handleCopyPrescription, setShowNewPrescription } = prescriptionActions;
    useEffect(() => {
        if (searchParams.get("openPrescription") !== "true" || !patient || loading) return;

        const repeatId = searchParams.get("repeat");
        if (repeatId) {
            const match = visits
                .map((v) => v.prescription)
                .find((p) => p && p.id === repeatId);
            if (match) handleCopyPrescription(match);
            else        setShowNewPrescription(true);
        } else {
            setShowNewPrescription(true);
        }

        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete("openPrescription");
            if (repeatId) next.delete("repeat");
            return next;
        });
    }, [patient, loading, visits, searchParams, setSearchParams, handleCopyPrescription, setShowNewPrescription]);


    // Track patient header height for sticky tab positioning
    useEffect(() => {
        const update = () => {
            if (patientHeaderRef.current) {
                const h = patientHeaderRef.current.getBoundingClientRect().height;
                document.documentElement.style.setProperty("--patient-header-height", `${h}px`);
            }
        };
        update();
        const ro = new ResizeObserver(update);
        if (patientHeaderRef.current) ro.observe(patientHeaderRef.current);
        return () => ro.disconnect();
    }, []);

    // ── Guards — all hooks above this line ──────────────────────────────────────

    const isInvalidId = !patientId || patientId === "new" || patientId.length < 5;
    if (isInvalidId) {
        return (
            <div className="prescription-form-container">
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="text-center">
                        <AlertTriangle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <h2 className="text-lg font-semibold text-foreground mb-2">Invalid Patient ID</h2>
                        <p className="text-muted-foreground mb-4">The patient ID provided is not valid.</p>
                        {onNavigateBack && (
                            <Button onClick={onNavigateBack}>
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Go Back
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="prescription-form-container">
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
                        <p className="text-muted-foreground">Loading patient details...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!patient) {
        return (
            <Card className="medical-card">
                <CardContent className="p-12 text-center">
                    <div className="max-w-md mx-auto">
                        <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
                            <User className="w-10 h-10 text-muted-foreground" />
                        </div>
                        <h3 className="text-xl font-semibold text-foreground mb-3">Patient Not Found</h3>
                        <p className="text-muted-foreground mb-6">
                            The patient you're looking for doesn't exist or couldn't be loaded.
                        </p>
                        <Button onClick={onNavigateBack || (() => window.history.back())}>
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Go Back to Patients
                        </Button>
                    </div>
                </CardContent>
            </Card>
        );
    }

    // ── Handlers ────────────────────────────────────────────────────────────────

    const handleEditPrescription = (prescription: { id: string }) =>
        navigate(`/prescriptions/edit/${prescription.id}`);

    const handleEditPatient = () => setShowEditModal(true);
    const handlePatientUpdated = () => { fetchPatientData(); setShowEditModal(false); };

    // ── Render ──────────────────────────────────────────────────────────────────

    return (
        <div className="prescription-form-container">
            <Tabs value={activeTab} onValueChange={setActiveTab}>

            {/* ── Unified sticky header (patient info + alerts + tabs) ── */}
            <div
                ref={patientHeaderRef}
                className="sticky z-[25] bg-card border-b border-border"
                style={{ top: "var(--topbar-height, 60px)" }}
            >
                {/* Main row */}
                <div className="flex items-center justify-between px-4 py-2 min-h-[42px]">

                    {/* Left — patient info */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[11px] font-bold flex-shrink-0">
                            {patient.name.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-sm font-semibold text-foreground">{patient.name}</span>
                        <span className="text-border">·</span>
                        <span className="text-xs text-muted-foreground">{displayAge(patient.age, patient.date_of_birth)} {patient.gender}</span>
                        {patient.blood_group && (
                            <span className="text-xs font-semibold text-destructive">{patient.blood_group}</span>
                        )}
                        {patient.mrn && (<>
                            <span className="text-border">·</span>
                            <span className="text-xs font-mono font-semibold text-primary">{patient.mrn}</span>
                        </>)}
                        {patient.phone && (<>
                            <span className="text-border">·</span>
                            <span className="text-xs text-muted-foreground">{patient.phone}</span>
                        </>)}
                        <span className="text-border">·</span>
                        <span className="text-xs text-muted-foreground">
                            {visits.length} visit{visits.length !== 1 ? "s" : ""}
                        </span>

                        {/* Scroll to past visits — prescription mode only */}
                        {prescriptionActions.showNewPrescription && visits.length > 0 && (
                            <button
                                type="button"
                                onClick={() => document.getElementById("previous-visits-ref")?.scrollIntoView({ behavior: "smooth" })}
                                className="text-xs text-primary font-medium flex items-center gap-0.5 hover:underline"
                            >
                                View Past <ChevronRight className="w-3 h-3" />
                            </button>
                        )}
                    </div>

                    {/* Right — actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {prescriptionActions.showNewPrescription && (
                            <span className="text-[11px] font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full border border-primary/20">
                                New Prescription
                            </span>
                        )}
                        {!prescriptionActions.showNewPrescription && onNavigateBack && (
                            <button
                                type="button"
                                onClick={onNavigateBack}
                                className="flex items-center gap-1 text-xs text-muted-foreground border border-border rounded px-2 py-1 hover:bg-muted/50 transition-colors"
                            >
                                <ArrowLeft className="w-3 h-3" /> Back
                            </button>
                        )}
                        {!prescriptionActions.showNewPrescription && currentUser?.doctor_id && (
                            <button
                                type="button"
                                onClick={prescriptionActions.handleNewPrescription}
                                className="flex items-center gap-1 text-xs font-semibold text-primary-foreground bg-primary hover:bg-primary/90 rounded px-3 py-1.5 transition-colors"
                            >
                                <Plus className="w-3 h-3" /> New Prescription
                            </button>
                        )}
                    </div>
                </div>

                {/* Alerts strip — always visible when patient has clinical alerts */}
                {(medicalAlerts.length > 0 || patient.current_medications || patient.allergies) && (
                    <div className="flex items-center gap-3 px-4 py-1 bg-warning/5 border-t border-warning/20 text-xs overflow-x-auto">
                        <div className="flex items-center gap-1 flex-shrink-0">
                            <AlertTriangle className="w-3 h-3 text-warning" />
                            <span className="font-semibold text-warning text-[11px]">Alerts</span>
                        </div>
                        {medicalAlerts.map((alert: any, i: number) => (
                            <span key={i} className="text-foreground whitespace-nowrap">
                                <strong>{alert.text}:</strong> {alert.details}
                            </span>
                        ))}
                        {patient.current_medications && (
                            <span className="text-foreground whitespace-nowrap">
                                <strong>Current Meds:</strong> {patient.current_medications}
                            </span>
                        )}
                    </div>
                )}

                {/* Tab bar — hidden during prescription mode */}
                {!prescriptionActions.showNewPrescription && (
                    <TabsList className={`grid w-full ${hasFeature('ipd') ? 'grid-cols-6' : 'grid-cols-5'} bg-transparent rounded-none h-auto p-0`}>
                        <TabsTrigger value="prescriptions" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-2">
                            <div className="flex items-center gap-2">
                                <NotebookPen className="w-4 h-4" />
                                <span className="text-xs sm:text-sm">Visits</span>
                            </div>
                        </TabsTrigger>
                        <TabsTrigger value="appointments" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-2">
                            <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                <span className="hidden sm:inline">Appointments</span>
                                <span className="sm:hidden text-xs">Appt</span>
                            </div>
                        </TabsTrigger>
                        <TabsTrigger value="profile" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-2">
                            <div className="flex items-center gap-2">
                                <User className="w-4 h-4" />
                                <span className="text-xs sm:text-sm">Profile</span>
                            </div>
                        </TabsTrigger>
                        <TabsTrigger value="billing" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-2">
                            <div className="flex items-center gap-2">
                                <IndianRupee className="w-4 h-4" />
                                <span className="hidden sm:inline">Billing</span>
                                <span className="sm:hidden text-xs">Bills</span>
                            </div>
                        </TabsTrigger>
                        {hasFeature('ipd') && (
                            <TabsTrigger value="ipd" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-2">
                                <div className="flex items-center gap-2">
                                    <BedIcon className="w-4 h-4" />
                                    <span className="text-xs sm:text-sm">IPD</span>
                                </div>
                            </TabsTrigger>
                        )}
                        <TabsTrigger value="documents" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-2">
                            <div className="flex items-center gap-2">
                                <Paperclip className="w-4 h-4" />
                                <span className="text-xs sm:text-sm">Documents</span>
                            </div>
                        </TabsTrigger>
                    </TabsList>
                )}
            </div>

            {/* ── Tab content ── */}
            {!prescriptionActions.showNewPrescription && (
                <>
                    {/* Visits Tab */}
                    <TabsContent value="prescriptions" className="px-3 py-3 mt-0">
                        <VisitTimeline
                            visits={visits}
                            onEditPrescription={handleEditPrescription}
                            onViewPrescription={prescriptionActions.handleViewPrescription}
                            onCopyPrescription={prescriptionActions.handleCopyPrescription}
                        />
                    </TabsContent>

                    {/* Appointments Tab */}
                    <TabsContent value="appointments">
                        <AppointmentsTab
                            appointments={appointments}
                            patientId={patientId}
                            patientName={patient.name}
                            onRefresh={fetchPatientData}
                        />
                    </TabsContent>

                    {/* Profile Tab */}
                    <TabsContent value="profile">
                        <PatientProfileTab patient={patient} onEdit={handleEditPatient} />
                    </TabsContent>

                    {/* Billing Tab */}
                    <TabsContent value="billing">
                        <BillingTab
                            patientId={patientId}
                            patient={{
                                id:      patient.id,
                                name:    patient.name,
                                phone:   patient.phone,
                                address: patient.address,
                            }}
                            doctorId={currentUser?.doctor_id || ""}
                            clinicId={currentUser?.clinic_id || ""}
                        />
                    </TabsContent>

                    {/* IPD Tab */}
                    {hasFeature('ipd') && (
                        <TabsContent value="ipd">
                            <PatientIPDTab
                                patientId={patientId}
                                patient={{
                                    name:   patient.name,
                                    age:    patient.age,
                                    gender: patient.gender,
                                    phone:  patient.phone,
                                    mrn:    patient.mrn,
                                }}
                                clinicId={currentUser?.clinic_id || ""}
                                canWrite={can('ipd:write')}
                            />
                        </TabsContent>
                    )}

                    {/* Documents Tab — patient-level, not tied to a specific visit */}
                    <TabsContent value="documents">
                        <PatientDocuments patientId={patientId} link={{}} />
                    </TabsContent>
                </>
            )}

            {/* ── Prescription Creation ── */}
            {prescriptionActions.showNewPrescription && (
                <>
                    {/* Form — no internal sticky bar */}
                    <PrescriptionForm
                        mode="create"
                        patientId={patientId}
                        initialData={prescriptionActions.copyData || undefined}
                        onSaved={() => { prescriptionActions.handlePrescriptionSaved(); setActiveTab("prescriptions"); }}
                        onCancel={handleNewPrescriptionCancel}
                        showStickyBar={false}
                        onFormReady={handleFormReady}
                        patient={patient}
                        doctor={doctor ?? undefined}
                    />

                    {/* Action bar — sticky bottom, tied to form.
                        Sticks while form fields are in view; scrolls off naturally
                        once the user passes into Previous Visits. */}
                    <FormActionBar
                        hasChanges={rxFormState.hasChanges}
                        hasValidChanges={rxFormState.hasValidChanges}
                        isSubmitting={rxFormState.isSubmitting}
                        mode="create"
                        onSubmit={() => rxSubmitRef.current?.()}
                        onCancel={handleNewPrescriptionCancel}
                        attachmentsSlot={rxFormState.attachmentsSlot}
                    />

                    {/* Previous Visits — separate, normal document flow */}
                    {visits.length > 0 && (
                        <div id="previous-visits-ref" className="bg-background border-t border-border/20">
                            <div className="px-3 py-2.5 border-b border-border/10 bg-primary/5 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Clock className="w-4 h-4 text-primary" />
                                    <h2 className="font-medium text-foreground text-sm">Previous Visits</h2>
                                </div>
                                <span className="text-xs text-muted-foreground">{visits.length} visit{visits.length !== 1 ? "s" : ""}</span>
                            </div>
                            <div className="p-3">
                                <VisitTimeline
                                    visits={visits}
                                    onEditPrescription={handleEditPrescription}
                                    onViewPrescription={prescriptionActions.handleViewPrescription}
                                    onCopyPrescription={prescriptionActions.handleCopyPrescription}
                                />
                            </div>
                        </div>
                    )}
                </>
            )}

            </Tabs>

            {/* ── Modals ── */}
            {prescriptionActions.viewingPrescription && (
                <ViewPrescriptionModal
                    open={true}
                    prescription={prescriptionActions.viewingPrescription}
                    onClose={prescriptionActions.handleCloseView}
                />
            )}

            {patient && (
                <PatientFormModal
                    isOpen={showEditModal}
                    onClose={() => setShowEditModal(false)}
                    onSuccess={handlePatientUpdated}
                    mode="edit"
                    patientId={patientId}
                    initialData={{
                        name:                    patient.name                    || "",
                        age:                     patient.age?.toString()         || "",
                        date_of_birth:           patient.date_of_birth           || "",
                        gender:                  patient.gender                  || "Male",
                        phone:                   patient.phone                   || "",
                        phone_2:                 patient.phone_2                 || "",
                        email:                   patient.email                   || "",
                        address:                 patient.address                 || "",
                        city:                    patient.city                    || "",
                        pin_code:                patient.pin_code                || "",
                        preferred_language:      patient.preferred_language      || "English",
                        aadhar_number:           patient.aadhar_number           || "",
                        blood_group:             patient.blood_group             || "",
                        allergies:               patient.allergies               || "",
                        emergency_contact_name:  patient.emergency_contact_name  || "",
                        emergency_contact_phone: patient.emergency_contact_phone || "",
                        payment_preference:      patient.payment_preference      || "Cash",
                    }}
                    showPrescriptionOption={false}
                />
            )}
        </div>
    );
}
